/**
 * Server <-> client chat message adapters for useChat.
 *
 * Extracted verbatim from useChat.ts (PR6 — pure-utils/data-mapping phase). No
 * behavior change (sed-lifted byte-for-byte); field mapping is byte-identical.
 * extractCreatedObjectId was previously wedged inside the import block at the top
 * of useChat.ts — unwedged here. Both are consumed by the hook body and re-imported.
 */

import type { ServerChatMessage } from '@/lib/api/endpoints/chat'
import type { ChatMessage } from '../hooks/useChat.types'
import { sanitizeHiddenDirectives } from './hidden-directives'

/** Robustly extract a created object ID from a tool_result payload.
 *  Handles strings, nested JSON strings, objects, and numeric IDs. */
export function extractCreatedObjectId(payload: unknown): string | undefined {
  let current = payload
  for (let depth = 0; depth < 4 && current != null; depth++) {
    if (typeof current === 'number') return String(current)
    if (typeof current === 'string') {
      const text = current.trim()
      if (!text) return undefined
      if ((text.startsWith('{') && text.endsWith('}')) || (text.startsWith('[') && text.endsWith(']'))) {
        try { current = JSON.parse(text); continue } catch { return undefined }
      }
      return undefined
    }
    if (Array.isArray(current)) { current = current[0]; continue }
    if (typeof current === 'object') {
      const obj = current as Record<string, unknown>
      const directId = obj.id ?? (obj.data as any)?.id ?? (obj.object as any)?.id ?? (obj.created as any)?.id
      if (typeof directId === 'string' || typeof directId === 'number') return String(directId)
      if (obj.result !== undefined) { current = obj.result; continue }
      if (obj.content !== undefined) { current = obj.content; continue }
      return undefined
    }
  }
  return undefined
}

/**
 * Convert server message to client message format
 */
export function serverToClientMessage(msg: ServerChatMessage): ChatMessage {
  const sanitized = sanitizeHiddenDirectives(String(msg.content || ''))
  return {
    id: msg.id,
    role: msg.role as 'user' | 'assistant',
    content: sanitized.content,
    timestamp: new Date(msg.createdAt),
    thinking: msg.thinking,
    toolsUsed: msg.toolsUsed,
    routing: msg.routing,
    model: undefined,
    durationMs: msg.durationMs,
    mode: msg.mode,
    attachments: msg.attachments?.map(a => ({
      filename: a.filename,
      fileType: a.fileType,
      assetId: a.assetId,
      url: a.url,
    })),
  }
}

